'use client';

import { SubmitButton } from '@/general/components/Button/SubmitButton';
import { FormWithAction } from '@/general/components/forms/FormWithAction/FormWithAction';
import { InputForm } from '@/general/components/forms/Input/InputForm';
import { InputSelectForm } from '@/general/components/forms/Input/InputSelectForm';
import { useRouter } from 'next/navigation';
import { FC, PropsWithChildren } from 'react';

import { AUSTRALIAN_STATES } from 'src/constants/constants';
import { RecaptchaNotice } from 'src/general/components/RecaptchaNotice/RecaptchaNotice';
import { Tabs } from 'src/general/components/Tabs/Tabs';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';

import { tradeInSearchCarRegoAction, tradeInSearchCarVINAction } from './TradeInSearchCar.action';

export const TradeInSearchCar: FC = () => {
  const router = useRouter();

  return (
    <div className="relative w-full overflow-hidden rounded-lg border border-bor-neu-bas bg-bac-tie1 shadow-elevation-0dp">
      <div className="bg-bac-tie2">
        <Tabs
          tabsId="ExchangeACar"
          tabs={[
            {
              id: 'rego',
              title: 'Registration plate',
              content: (
                <TradeInFormWrapper>
                  <FormWithAction
                    action={tradeInSearchCarRegoAction}
                    onSuccess={(success) => {
                      pushToDataLayer({
                        event: 'trade_in_step_car_lookup',
                        type: 'Registration',
                      });
                      router.push(success.message);
                    }}
                    renderContent={(state) => (
                      <>
                        <InputForm
                          formState={state}
                          id="rego"
                          name="rego"
                          type="text"
                          label="Registration plate"
                          data-testid="registration-plate"
                        />
                        <InputSelectForm
                          formState={state}
                          id="state"
                          name="state"
                          label="State of registration"
                          data-testid="state-of-registration"
                          allowEmpty
                          options={AUSTRALIAN_STATES.map((state) => ({ label: state, value: state }))}
                        />
                        <TradeInFormSubmit testId="ExchangeACar-component-continueButton" />
                      </>
                    )}
                  />
                </TradeInFormWrapper>
              ),
            },
            {
              id: 'vin',
              title: 'VIN',
              content: (
                <TradeInFormWrapper>
                  <FormWithAction
                    action={tradeInSearchCarVINAction}
                    onSuccess={(success) => {
                      pushToDataLayer({
                        event: 'trade_in_step_car_lookup',
                        type: 'VIN',
                      });
                      router.push(success.message);
                    }}
                    renderContent={(state) => (
                      <>
                        <InputForm formState={state} id="vin" name="vin" type="text" label="VIN" data-testid="vin" />
                        <div
                          className="c-fs-caption text-tex-def-tie2"
                          data-testid="HYGIENE_TRADE_IN_BANNER_TAB_HELP_VIN"
                        >
                          The VIN can generally be located on the body of the vehicle, under the bonnet, at the bottom
                          of the windscreen on the passenger side, along the drivers side door closure area, or on your
                          registration papers.
                        </div>
                        <TradeInFormSubmit testId="ExchangeACar-component-vinContinueButton" />
                      </>
                    )}
                  />
                </TradeInFormWrapper>
              ),
            },
          ]}
          fullWidth
        />
      </div>
    </div>
  );
};

const TradeInFormWrapper: FC<PropsWithChildren> = ({ children }) => (
  <div className="min-h-82 bg-bac-tie1 p-6">{children}</div>
);

const TradeInFormSubmit: FC<{ testId: string }> = ({ testId }) => (
  <div className="mt-6 grid grid-cols-1 gap-4">
    <SubmitButton variant="accent" fullWidth data-testid={testId}>
      Value my car
    </SubmitButton>
    <RecaptchaNotice />
  </div>
);
